import axios from 'axios';
import { Config } from 'domain/configs/models/config';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { getAppUsers } from 'services/app';
import { listConfigCatalog, searchConfigs } from 'services/config';
import { listMarketplaceByIdi } from 'services/integratedPayment';
import { Mixpanel } from 'services/mixpanel';
import { App } from 'shared/models/app';
import { AppUser } from 'shared/models/appUser';
import { Marketplace } from 'shared/models/marketplace';
import { Driver } from 'shared/types/marketplace';
import AppTab from './AppTab';
import getPixDriver from './cards/IntegratedPayment/helpers/getPixDriver';
import AppStatusHistoryModal from './components/AppStatusHistoryModal';
import CreateTempUserModal from './components/CreateTempUserModal';
import EditAppDetailsModal from './components/EditAppDetailsModal';
import EditFiscalModal from './components/EditFiscalModal';
import PauseModal from './components/PauseModal';
import ResetModal from './components/ResetModal';
import TransferInstallationModal from './components/TransferInstallationModal';
import { mergeConfigCatalogWithValues } from './helpers/mergeConfigCatalogWithValues';

interface AppProps {
  app: App;
  isEmissor?: boolean;
}

interface AppContextData {
  app: App;
  setApp(app: App): void;
  openCreateTempUserModal(): void;
  openEditAppDetailsModal(): void;
  openEditFiscalModal(): void;
  users: AppUser[];
  loadingUsers: boolean;
  loadUsers(): void;
  rawConfigs: Config[];
  loadingConfigs: boolean;
  setLoadingConfigs(b: boolean): void;
  updateConfig(
    configs: { name: string; value: boolean | number | string }[],
  ): void;
  isEmissor: boolean;
  openResetModal(): void;
  openPauseModal(): void;
  openTransferModal(): void;
  openAppStatusHistoryModal(): void;
  setAppStatusHistory(b: boolean): void;
  isLoadingLogs: boolean;
  setIsLoadingLogs(b: boolean): void;
  isLoadingIntegratedPayment: boolean;
  setIsLoadingIntegratedPayment(b: boolean): void;
  getDriver: Driver | null;
  marketplace: Marketplace;
}

const Context = createContext<AppContextData>({} as AppContextData);

export const useAppTabContext = () => useContext(Context);

const AppContext: React.FC<AppProps> = ({
  app: appFromProps,
  isEmissor = false,
}) => {
  const [app, setApp] = useState<App>(appFromProps);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [rawConfigs, setRawConfigs] = useState<Config[]>([]);

  const [createUserModal, setCreateUserModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editFiscalModal, setEditFiscalModal] = useState(false);
  const [appStatusHistoryModal, setAppStatusHistory] = useState(false);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const [isLoadingIntegratedPayment, setIsLoadingIntegratedPayment] =
    useState(false);

  const [marketplace, setMarketplace] = useState<Marketplace>(
    {} as Marketplace,
  );
  const [driver, setDriver] = useState<Driver | null>(null);

  const openCreateTempUserModal = () => {
    Mixpanel.track('customers-app-users-opened-temp-user');
    setCreateUserModal(true);
  };

  const openEditAppDetailsModal = () => {
    Mixpanel.track('customers-app-details-opened-edit');
    setEditModal(true);
  };
  const openEditFiscalModal = () => {
    Mixpanel.track('customers-app-fiscal-opened-edit');
    setEditFiscalModal(true);
  };

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);

  const openResetModal = () => {
    Mixpanel.track('customers-app-danger-zone-opened-reset');
    setResetModalOpen(true);
  };

  const onCloseResetModal = () => setResetModalOpen(false);

  const openPauseModal = () => {
    setPauseModalOpen(true);
  };

  const onClosePauseModal = () => setPauseModalOpen(false);

  const openTransferModal = () => {
    setTransferModalOpen(true);
  };

  const onCloseTransferModal = () => setTransferModalOpen(false);

  const openAppStatusHistoryModal = () => {
    setAppStatusHistory(true);
  };

  const updateConfig = (
    configs: { name: string; value: boolean | number | string }[],
  ) => {
    setRawConfigs(
      rawConfigs.map((config) => {
        const configFound = configs.find((c) => c.name === config.name);
        if (configFound) {
          config.trueValue = configFound.value.toString();
        }

        return config;
      }),
    );
  };

  const loadUsers = useCallback(() => {
    setLoadingUsers(true);
    getAppUsers(app.idi)
      .then((response) =>
        setUsers(
          response.data.results.map((appUserData) => new AppUser(appUserData)),
        ),
      )
      .catch(() => toast.error('Falha ao buscar usuários'))
      .finally(() => setLoadingUsers(false));
  }, [app]);

  const loadConfigs = useCallback(async () => {
    setLoadingConfigs(true);

    try {
      const catalogResponse = await listConfigCatalog();

      try {
        const valuesResponse = await searchConfigs(app.idi);
        setRawConfigs(
          mergeConfigCatalogWithValues(
            catalogResponse.data.results,
            valuesResponse.data.configs,
          ),
        );
      } catch {
        toast.error('Falha ao carregar as configurações');
      }
    } catch {
      toast.error('Falha ao carregar o catálogo de configurações');
    } finally {
      setLoadingConfigs(false);
    }
  }, [app]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    listMarketplaceByIdi(String(app.idi))
      .then((res) => {
        const marketplaceData = new Marketplace(res.data);
        setMarketplace(marketplaceData);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status !== 404) {
          console.error('Falha ao carregar o marketplace', error);
        }
      });
  }, [app.idi]);

  useEffect(() => {
    if (marketplace.drivers) {
      setDriver(getPixDriver(marketplace.drivers));
    }
  }, [marketplace.drivers]);

  const data: AppContextData = {
    app,
    setApp,
    openCreateTempUserModal,
    openEditAppDetailsModal,
    openEditFiscalModal,
    users,
    loadingUsers,
    loadUsers,
    rawConfigs,
    loadingConfigs,
    setLoadingConfigs,
    updateConfig,
    isEmissor,
    openResetModal,
    openPauseModal,
    openTransferModal,
    openAppStatusHistoryModal,
    setAppStatusHistory,
    isLoadingLogs,
    setIsLoadingLogs,
    isLoadingIntegratedPayment,
    setIsLoadingIntegratedPayment,
    getDriver: driver,
    marketplace,
  };

  return (
    <Context.Provider value={data}>
      <AppTab />

      <CreateTempUserModal
        isOpen={createUserModal}
        onClose={() => setCreateUserModal(false)}
      />

      <EditAppDetailsModal
        isOpen={editModal}
        onClose={() => setEditModal(false)}
      />

      <EditFiscalModal
        isOpen={editFiscalModal}
        onClose={() => setEditFiscalModal(false)}
      />

      <AppStatusHistoryModal
        isOpen={appStatusHistoryModal}
        onClose={() => setAppStatusHistory(false)}
      />

      <ResetModal isOpen={resetModalOpen} onClose={onCloseResetModal} />
      <PauseModal isOpen={pauseModalOpen} onClose={onClosePauseModal} />
      <TransferInstallationModal
        isOpen={transferModalOpen}
        onClose={onCloseTransferModal}
      />
    </Context.Provider>
  );
};

export default AppContext;
